import { Dialog } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  getDealTargetedChannelGroupsPromise,
  getDealTargetedSitesPromise,
} from '~/src/dash/api/channels';
import { INameID } from '~/src/dash/api/console-api';
import { deleteDeal } from '~/src/dash/api/deals';
import { EBool } from '~/src/dash/api/types/bool';
import {
  getDealsList,
  ISimpleDeal,
  TDealListOrderByKey,
  updateDealActiveStatus,
} from '~/src/dash/apiV2/deals';
import { IStartToEndDateRanges } from '~/src/dash/components/calendar/common';
import { validateDateRanges } from '~/src/dash/components/calendar/common';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { formatSelectedFiltersIntoQueryObject } from '~/src/dash/components/filters/conversions';
import { DateRangeRow } from '~/src/dash/components/filters/DateRangeRow';
import { FilterRowV2 } from '~/src/dash/components/filters/FilterRowV2';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractVisibleColumns,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelector } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelector';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextInput,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  getRoutePath,
  IInventoryRouteParams,
  navigate,
  RouteChannelExisting,
  RouteDealEdit,
  RouteDealNew,
  RouteDealTPAccountList,
} from '~/src/dash/router/routes';
import { RouteDealCreate } from '~/src/dash/router/routes';
import { isSameMoment } from '~/src/dash/utils/calendar';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { useBaseConfig } from '~/src/dash/views/base/contexts';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { ESelectedModal } from '~/src/dash/views/deal/dealsV2/DealsListView/common';
import { formatDealsResponseIntoLVTRows } from '~/src/dash/views/deal/dealsV2/DealsListView/config';
import { DealsListContext } from '~/src/dash/views/deal/dealsV2/DealsListView/contexts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { TopBar } from '~/src/dash/views/topbar/TopBar';

export const DealsListView = (): React.ReactNode => {
  const { session, user, isSuperUser, isDemoUser } = useBaseStore();
  const { t } = usePublicaTranslation();
  const { Deals } = useBaseConfig();
  const cfg = Deals.Main;
  const readAuthorization = IS_AUTHORIZED(['read_deal'], user.user);
  const writeAuthorization = IS_AUTHORIZED(['write_deal'], user.user);

  const isMounted = useRef(false);

  const [startToEndDateRanges, setStartToEndDateRanges] = useState<IStartToEndDateRanges>(
    cfg.startToEndDateRanges,
  );
  const [areDateRangesInvalid, setAreDateRangesInvalid] = useState(false);

  const [filtersLoadState, setFiltersLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [filters, setFilters] = useState<Record<string, ISelectOption[]>>({});
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string | number>>>({});
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    cfg.timezone || session.publisher.Timezone,
  );

  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    cfg.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.columns),
  );

  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [responseData, setResponseData] = useState<ISimpleDeal[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [isTableActionPending, setIsTableActionPending] = useState(false);

  const [query, setQuery] = useState('');

  const [order, setOrder] = useState<Order>(cfg.table.order || 'asc');
  const [orderBy, setOrderBy] = useState<TDealListOrderByKey>(
    (cfg.table.orderBy as TDealListOrderByKey) || 'start_date',
  );
  const [page, setPage] = useState<number>(cfg.table.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(cfg.table.rowsPerPage || 10);

  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [snackbarWarning, setSnackbarWarning] = useState(false);
  // modal states
  const [activeDeal, setActiveDeal] = useState<number | undefined>(undefined);
  const [activeModal, setActiveModal] = useState<ESelectedModal | undefined>(undefined);
  const [activeTargeted, setActiveTargeted] = useState<INameID[]>([]);
  const [isChannelsModalActive, setIsChannelsModalActive] = useState<boolean>(false);
  const [isChannelGroupsModalActive, setIsChannelGroupsModalActive] = useState<boolean>(false);

  const populateFilters = (): void => {
    setFiltersLoadState(LoadingStateLoading);
    const keys: string[] = [];
    const getListFns: Array<Promise<ISelectOption[]>> = [];

    Object.values(cfg.filters).forEach((filter) => {
      keys.push(filter.key);
      getListFns.push(filter.getList(session, user));
    });

    const tempFilters: Record<string, ISelectOption[]> = {};
    const tempSelectedFilters: Record<string, Set<string | number>> = {};

    Promise.all(getListFns)
      .then((results) => {
        results.forEach((result, index) => {
          const filterKey = keys[index];
          tempFilters[filterKey] = result;
          tempSelectedFilters[filterKey] =
            cfg.filters[filterKey].selectedOptionIDs || new Set<string | number>();
        });
        unstable_batchedUpdates(() => {
          setFilters(tempFilters);
          setFiltersLoadState(LoadingStateHasData);
          setSelectedFilters(tempSelectedFilters);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setFilters({});
          setSelectedFilters({});
          setFiltersLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarWarning(false);
          setSnackbarMessage(t('SnackbarErrorRetrievingItem', { item: t('Filters') }));
        });
      });
  };

  const getData = (): void => {
    const startTime = moment().unix();
    setLoadState(LoadingStateLoading);
    const filterParams = formatSelectedFiltersIntoQueryObject(selectedFilters, cfg.filters);

    void getDealsList(session.publisher.ID, {
      publisher_id: session.publisher.ID,
      search: query,
      page_size: rowsPerPage,
      page,
      order_direction: order,
      order_by: orderBy,
      active: EBool.EBool_TRUE,
      status: [],
      timezone: selectedTimezone,
      start_date_from: startToEndDateRanges.startDateFrom?.format('YYYY-MM-DD') || '',
      start_date_to: startToEndDateRanges.startDateTo?.format('YYYY-MM-DD') || '',
      end_date_from: startToEndDateRanges.endDateFrom?.format('YYYY-MM-DD') || '',
      end_date_to: startToEndDateRanges.endDateTo?.format('YYYY-MM-DD') || '',
      deal_tiers: [],
      salesperson_ids: [],
      channel_ids: [],
      channel_group_ids: [],
      deal_types: [],
      partners: [],
      ...filterParams,
    })
      .then(({ deals, enrichmentErrors, totalCount }) => {
        const loadTime = moment().unix() - startTime;
        track(
          'Deals List - List Displayed',
          {
            isSuperUser,
            loadTime,
            numRowsDisplayed: deals.length,
            totalRowCount: totalCount,
          },
          user.user,
        );
        unstable_batchedUpdates(() => {
          setResponseData(deals);
          setTotalRowCount(totalCount);
          setLoadState(LoadingStateHasData);
          if (enrichmentErrors?.length > 0) {
            setSnackbarWarning(true);
            setSnackbarError(false);
            setSnackbarMessage(enrichmentErrors.join(', '));
          }
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setResponseData([]);
          setTotalRowCount(0);
          setLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarWarning(false);
          setSnackbarMessage(t('SnackbarErrorRetrievingItem', { item: t('Deals') }));
        });
      });
  };

  const handleApplyDateRanges = (ranges: IStartToEndDateRanges): void => {
    const { startDateFrom, startDateTo, endDateFrom, endDateTo } = ranges;
    // Only update state if the values are different
    const hasChanged =
      !isSameMoment(startToEndDateRanges.startDateFrom, startDateFrom) ||
      !isSameMoment(startToEndDateRanges.startDateTo, startDateTo) ||
      !isSameMoment(startToEndDateRanges.endDateFrom, endDateFrom) ||
      !isSameMoment(startToEndDateRanges.endDateTo, endDateTo);

    if (hasChanged) {
      // validate date ranges
      const { isValid, errorTKey, errorTProps } = validateDateRanges(ranges);
      if (!isValid) {
        unstable_batchedUpdates(() => {
          setSnackbarMessage(t(errorTKey, errorTProps));
          setSnackbarError(true);
          setSnackbarWarning(false);
          setAreDateRangesInvalid(true);
        });
      } else {
        unstable_batchedUpdates(() => {
          setStartToEndDateRanges(ranges);
          setAreDateRangesInvalid(false);
          setPage(0);
        });
        cfg.startToEndDateRanges = ranges;
      }
    } else if (!startDateFrom && !startDateTo && !endDateFrom && !endDateTo) {
      setAreDateRangesInvalid(false);
    }
  };

  const handleFilterAccept = (value: Set<number | string>, key: string): void => {
    if (
      selectedFilters[key].size === value.size &&
      [...selectedFilters[key]].every((item) => value.has(item))
    ) {
      // No change, do nothing
      return;
    } else {
      const clonedSelectedFilters = { ...selectedFilters };
      clonedSelectedFilters[key] = value;
      unstable_batchedUpdates(() => {
        setSelectedFilters(clonedSelectedFilters);
        setPage(0);
      });
      cfg.filters[key].selectedOptionIDs = value;
      cfg.table.page = 0;
      if (value.size) {
        track('Deals List - Filter', { isSuperUser, filter: key }, user.user);
      }
    }
  };

  const handleClearFilters = (): void => {
    const clearedSelectedFilters: Record<string, Set<string | number>> = {};
    Object.values(cfg.filters).forEach((filter) => {
      const resetValue = new Set<string | number>(filter.selectedOptionIDsOnReset || []);
      clearedSelectedFilters[filter.key] = resetValue;
      cfg.filters[filter.key].selectedOptionIDs = resetValue;
    });
    unstable_batchedUpdates(() => {
      setSelectedFilters(clearedSelectedFilters);
      setPage(0);
    });
    cfg.table.page = 0;
  };

  const handleApplyTimezone = (timezone: Set<number | string>): void => {
    setSelectedTimezone(timezone.values().next().value as string);
  };

  const handleOnChangeQuery = (text: string): void => {
    unstable_batchedUpdates(() => {
      setQuery(text);
      setPage(0);
    });
  };

  const handleChangeActiveColumns = (keys: string[]): void => {
    setActiveColumnKeys(keys);
  };

  const handleAcceptActiveColumns = (keys: string[]): void => {
    track('Deals List - Active Columns', { isSuperUser, columns: keys }, user.user);
    setActiveColumnKeys(keys);
    cfg.activeColumnKeys = keys;
  };

  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key as TDealListOrderByKey);
        setPage(0);
      });
      cfg.table.page = 0;
      cfg.table.order = defaultOrderDirection || 'asc';
      cfg.table.orderBy = key as TDealListOrderByKey;
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
      cfg.table.page = 0;
      cfg.table.order = isAsc ? 'desc' : 'asc';
      cfg.table.orderBy = key;
    }
  };

  const handleOnChangeStatus = (id: string, checked: boolean, row: IListViewTableRow): void => {
    setIsTableActionPending(true);
    updateDealActiveStatus(session.publisher.ID, Number(id), {
      Active: checked ? EBool.EBool_TRUE : EBool.EBool_FALSE,
    })
      .then(() => {
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(
            t('SnackbarSuccessfullyUpdatedIDName', {
              id: String(id),
              name: (row?.Name?.value as string) || t('Deal'),
            }),
          );
          setSnackbarError(false);
        });
        getData();
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(
            t('SnackbarFailedToUpdateIDName', {
              id: String(id),
              name: (row?.Name?.value as string) || t('Deal'),
            }),
          );
          setSnackbarError(true);
          setSnackbarWarning(false);
        });
      });
  };

  const handleClickOnChannelsList = (id: string): void => {
    track('Deals List - Open Modal', { isSuperUser, modal: 'Targeted Channels' }, user.user);
    unstable_batchedUpdates(() => {
      setActiveDeal(Number(id));
      setActiveModal(ESelectedModal.TargetedChannels);
    });
  };

  const handleClickOnChannelGroupsList = (id: string): void => {
    track('Deals List - Open Modal', { isSuperUser, modal: 'Targeted Channel Groups' }, user.user);
    unstable_batchedUpdates(() => {
      setActiveDeal(Number(id));
      setActiveModal(ESelectedModal.TargetedChannelGroups);
    });
  };

  const handleCloseModals = (): void => {
    unstable_batchedUpdates(() => {
      setActiveDeal(undefined);
      setActiveModal(undefined);
      setActiveTargeted([]);
      setIsChannelsModalActive(false);
      setIsChannelGroupsModalActive(false);
    });
  };

  const getDealSettingsRoute = (id: string): string => {
    return getRoutePath(RouteDealEdit, extractRouteParams({ session, user }), {
      dealID: id,
    });
  };

  const handleCloneDeal = (id: string): void => {
    // Use query parameter approach like the original implementation
    window.location.href =
      '/#' +
      getRoutePath(RouteDealNew, { pub: session.publisher.ID } as IInventoryRouteParams, {}) +
      `?cloneId=${id}`;
  };

  const handleDeleteDeal = (id: string): Promise<unknown> => {
    setIsTableActionPending(true);
    deleteDeal(Number(id), session.publisher.ID)
      .then(() => {
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(t('SnackbarSuccessfullyDeletedItem', { item: t('Deal') }));
          setSnackbarError(false);
        });
        getData();
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(t('SnackbarFailedToDeleteItem', { item: t('Deal') }));
          setSnackbarError(true);
          setSnackbarWarning(false);
        });
      });
    return new Promise((resolve) => resolve(undefined));
  };

  const handleChangePage = (_e: unknown, newPage: number): void => {
    setPage(newPage);
    cfg.table.page = newPage;
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    });
    cfg.table.rowsPerPage = updatedRowsPerPage;
    cfg.table.page = 0;
  };

  const navigateToNewDealView = (): void => {
    track('Deal List - New Deal', { isSuperUser }, user.user);
    navigate(RouteDealCreate, extractRouteParams({ session, user }));
  };

  const navigateToManageThirdPartyAccounts = (): void => {
    track('Deal List - Manage Third Party Accounts', { isSuperUser }, user.user);
    navigate(RouteDealTPAccountList, extractRouteParams({ session, user }));
  };

  useEffect(() => {
    if (isMounted.current && activeDeal) {
      // if targeted channels click
      if (activeModal === ESelectedModal.TargetedChannels) {
        getDealTargetedSitesPromise({
          publisherID: session.publisher.ID,
          dealID: activeDeal,
        })
          .then((result: INameID[]) => {
            unstable_batchedUpdates(() => {
              setActiveTargeted(result);
            });
          })
          .catch((err) => {
            console.error(err);
          });
      } else if (activeModal === ESelectedModal.TargetedChannelGroups) {
        getDealTargetedChannelGroupsPromise({
          publisherID: session.publisher.ID,
          dealID: activeDeal,
        })
          .then((result: INameID[]) => {
            unstable_batchedUpdates(() => {
              setActiveTargeted(result);
            });
          })
          .catch((err) => {
            console.error(err);
          });
      } else {
        setActiveTargeted([]);
      }
    }
  }, [activeDeal, activeModal, session.publisher.ID]);

  useEffect(() => {
    if (isMounted.current && !!activeDeal) {
      switch (activeModal) {
        case ESelectedModal.TargetedChannels:
          setIsChannelsModalActive(true);
          break;
        case ESelectedModal.TargetedChannelGroups:
          setIsChannelGroupsModalActive(true);
          break;
        default:
          break;
      }
    }
  }, [activeTargeted]);

  useEffect(() => {
    populateFilters();
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      getData();
    }
  }, [
    query,
    order,
    orderBy,
    page,
    rowsPerPage,
    startToEndDateRanges,
    selectedFilters,
    selectedTimezone,
  ]);

  useEffect(() => {
    isMounted.current = true;
  }, []);

  const activeColumnsList = useMemo(
    () => extractVisibleColumns(cfg.columns, activeColumnKeys, isSuperUser),
    [],
  );
  const rows = formatDealsResponseIntoLVTRows(responseData, cfg.columns);

  return (
    <DealsListContext.Provider value={{ cfg, readAuthorization, writeAuthorization }}>
      <Panels
        max
        vertical
        relative
        minWidthMain
        largeHorizontalPadding
        style={{ rowGap: spacing.base }}
      >
        <Panel smallVerticalPadding>
          <TopBar title='Deals' enableUINotices tKey={'Deals'} />
        </Panel>
        <Panel>
          <Cols
            wrap
            gap={spacing.small}
            style={{
              justifyContent: 'flex-end',
              alignItems: 'center',
              rowGap: spacing.small,
            }}
          >
            <Col>
              <ButtonV2 onClick={navigateToManageThirdPartyAccounts}>
                <Text inheritColor inheritWeight tKey='ManageThirdPartyAccounts' />
              </ButtonV2>
            </Col>
            <Col>
              <ButtonV2 onClick={navigateToNewDealView}>
                <Cols
                  gap={spacing.small}
                  style={{
                    alignItems: 'center',
                  }}
                >
                  <Col displayFlex>
                    <IconV2 name='add' color={publicaColors.white} />
                  </Col>
                  <Col>
                    <Text inheritColor inheritWeight tKey='NewItem' tProps={{ item: t('Deal') }} />
                  </Col>
                </Cols>
              </ButtonV2>
            </Col>
          </Cols>
        </Panel>
        <Panel highlighted>
          <Rows>
            <Row noPadding style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
              <DateRangeRow
                cypressParentName={cfg.cypressClassName}
                selectedRanges={startToEndDateRanges}
                handleApplyDateRanges={handleApplyDateRanges}
                hasError={areDateRangesInvalid}
                defaultTimezone={selectedTimezone}
                handleApplyTimezone={handleApplyTimezone}
              />
            </Row>
            <Row noPadding>
              <FilterRowV2
                cypressParentName={cfg.cypressClassName}
                enableClear
                filtersConfig={cfg.filters}
                filtersToOptionsMap={filters}
                handleClearFilters={handleClearFilters}
                handleApplySelect={handleFilterAccept}
                selectedFilters={selectedFilters}
                loadState={filtersLoadState}
              />
            </Row>
          </Rows>
        </Panel>
        <Panel highlighted largeHorizontalPadding verticalPadding>
          <Rows>
            <Row noPadding>
              <Cols style={{ justifyContent: 'space-between' }}>
                <Col>
                  <Cols
                    wrap
                    gap={spacing.medium}
                    style={{
                      alignItems: 'center',
                      rowGap: spacing.small,
                    }}
                  >
                    <Col>
                      <TextInput
                        enableClear
                        handleOnChange={handleOnChangeQuery}
                        msDelay={500}
                        placeholder={t('SearchByNameID')}
                        prefix='search'
                      />
                    </Col>
                    <Col>
                      <SearchResultsText
                        numRowsDisplayed={responseData.length}
                        numTotalRows={totalRowCount}
                      />
                    </Col>
                  </Cols>
                </Col>
                <Col>
                  <ColumnSelector
                    preselectedColumnKeys={activeColumnKeys}
                    resetActiveColumnKeys={cfg.columnSelector.resetKeys}
                    columnsList={activeColumnsList}
                    columnsHandleOnChangeOption={handleChangeActiveColumns}
                    columnsHandleOnAccept={handleAcceptActiveColumns}
                  />
                </Col>
              </Cols>
            </Row>
            <Row noHorizontalPadding verticalPadding>
              <ListViewTable
                activeColumnKeys={activeColumnKeys}
                columns={cfg.columns}
                displayNameWithID={!isDemoUser}
                deleteItemName={t('Deal')}
                customFunctions={{
                  handleClickOnChannelsList,
                  handleClickOnChannelGroupsList,
                }}
                enableActions
                enableStatusSwitch
                enableTrackingOpenObject
                getItemSettingsRoute={getDealSettingsRoute}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handleCloneItem={handleCloneDeal}
                handleDeleteItem={handleDeleteDeal}
                handleOnChangeStatus={handleOnChangeStatus}
                handleOnClickColumnHeader={handleOnClickColumnHeader}
                isActionPending={isTableActionPending}
                isReadAuthorized={readAuthorization.isAuthorized}
                isSuperUser={isSuperUser}
                isWriteAuthorized={writeAuthorization.isAuthorized}
                loadState={loadState}
                order={order}
                orderBy={orderBy}
                page={page}
                rows={rows}
                rowsPerPage={rowsPerPage}
                tableName={'Deals List'}
                totalRowCount={totalRowCount}
                className={cfg.cypressClassName}
              />
            </Row>
          </Rows>
        </Panel>
        <Dialog
          fullWidth
          maxWidth='md'
          keepMounted
          open={isChannelsModalActive}
          onClose={handleCloseModals}
        >
          <ModalSelectTable
            title={t('TargetingChannels')}
            singularTitle={t('Channel')}
            items={activeTargeted}
            isExplicitLink={true}
            onSelectRow={(siteID: string) => {
              navigate(RouteChannelExisting, extractRouteParams({ session, user }), {
                ch: siteID,
              });
            }}
            interactive={true}
          />
        </Dialog>
        <Dialog
          fullWidth
          maxWidth='md'
          keepMounted
          open={isChannelGroupsModalActive}
          onClose={handleCloseModals}
        >
          <ModalSelectTable
            title={t('TargetingChannelGroups')}
            singularTitle={t('ChannelGroup')}
            items={activeTargeted}
            interactive={false}
          />
        </Dialog>
        <SnackbarView
          open={!!snackbarMessage}
          error={!!snackbarError}
          warning={!!snackbarWarning}
          onClose={() => setSnackbarMessage('')}
        >
          {snackbarMessage}
        </SnackbarView>
        <PanelBreak height={spacing.large} />
      </Panels>
    </DealsListContext.Provider>
  );
};
